import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { SocketService } from 'src/app/core/service/socket.service';
import { AuthService } from 'src/app/auth/auth.service';
import {
  ConversationType,
  JoinChatRoomRequest
} from '../interface/chat.interface';

/**
 * @description Service for managing chat WebSocket functionality
 * Follows the same pattern as other socket services in the application
 */
@Injectable({
  providedIn: 'root'
})
export class ChatSocketService {
  // Track active chat rooms (conversation IDs)
  private readonly activeChatRooms: Set<number> = new Set();

  constructor(
    private readonly socketService: SocketService,
    private readonly authService: AuthService
  ) {}

  /**
   * @description Build room name according to WebSocket implementation guide
   * Room format: chat:one_on_one:{id} or chat:event_group:{id}
   * @param conversationId - Conversation ID
   * @param conversationType - Conversation type
   * @returns Room name string
   */
  private buildChatRoomName(conversationId: number, conversationType: ConversationType): string {
    return conversationType === 'ONE_ON_ONE'
      ? `chat:one_on_one:${conversationId}`
      : `chat:event_group:${conversationId}`;
  }

  /**
   * @description Join a chat room for a conversation
   * Follows the WebSocket implementation guide pattern
   * Room names: chat:one_on_one:{id} or chat:event_group:{id}
   * @param request - Join chat room request
   */
  joinChatRoom(request: JoinChatRoomRequest): void {
    if (!this.socketService.isConnected()) {
      console.warn('⚠️ Socket not connected, cannot join chat room');
      // Wait for socket connection and retry
      this.socketService.connectionStatus.subscribe((connected: boolean) => {
        if (connected) {
          this.joinChatRoom(request);
        }
      });
      return;
    }

    // Build room name according to guide: chat:one_on_one:{id} or chat:event_group:{id}
    const roomName = this.buildChatRoomName(request.conversationId, request.conversationType);

    console.log('🚪 Joining chat room:', roomName, request);
    
    // Use ONLY joinChatRoom event (like joinBidRoom) - backend requires this for chat rooms
    // joinRoom method causes permission denied errors for chat rooms
    const socket = this.socketService.getSocket();
    if (socket?.connected) {
      console.log(`[ChatSocket] Joining chat room via joinChatRoom event: ${roomName}`);
      socket.emit('joinChatRoom', {
        conversationId: request.conversationId,
        conversationUuid: request.conversationUuid,
        conversationType: request.conversationType
      }, (response: any) => {
        console.log(`[ChatSocket] joinChatRoom callback for ${roomName}:`, response);
        if (response?.success !== false && !response?.error) {
          this.trackChatRoomJoined(request.conversationId);
          console.log(`✅ Successfully joined chat room via joinChatRoom: ${roomName}`, response);
        } else {
          console.error(`❌ Failed to join chat room via joinChatRoom: ${roomName}`, response);
        }
      });
      
      // Track optimistically (will be confirmed by chat_room_joined event)
      this.trackChatRoomJoined(request.conversationId);

      // Note: User-specific rooms (chat:user:{userId}, system:user:{userId}) are likely
      // auto-joined by backend or joined via a different mechanism. The backend broadcasts
      // to these rooms, but we don't need to explicitly join them via joinRoom (which fails).
    } else {
      console.error(`[ChatSocket] Cannot join room - socket not connected:`, {
        hasSocket: !!socket,
        isConnected: socket?.connected
      });
    }
  }

  /**
   * @description Leave a chat room
   * Follows the WebSocket implementation guide pattern
   * Room names: chat:one_on_one:{id} or chat:event_group:{id}
   * @param conversationId - Conversation ID
   * @param conversationType - Conversation type
   */
  leaveChatRoom(conversationId: number, conversationType: ConversationType): void {
    if (!this.socketService.isConnected()) {
      console.warn('⚠️ Socket not connected, cannot leave chat room');
      // Remove from tracking even if socket not connected
      this.trackChatRoomLeft(conversationId);
      return;
    }

    // Build room name according to guide: chat:one_on_one:{id} or chat:event_group:{id}
    const roomName = this.buildChatRoomName(conversationId, conversationType);

    console.log('🚪 Leaving chat room:', roomName);
    
    // According to the WebSocket guide, chat rooms use direct 'leave' event with room name string
    const socket = this.socketService.getSocket();
    if (socket?.connected) {
      console.log(`[ChatSocket] Leaving room using direct 'leave' event: ${roomName}`);
      
      // Use direct 'leave' event as per the guide: socket.emit('leave', roomName)
      socket.emit('leave', roomName, (response: any) => {
        // Remove from tracking
        this.trackChatRoomLeft(conversationId);
        console.log(`✅ Left chat room: ${roomName}`, response);
      });
    }
  }

  /**
   * @description Start typing indicator
   * Follows the same pattern as event/bid socket emits
   * @param conversationId - Conversation ID
   * @param conversationType - Conversation type
   */
  startTyping(conversationId: number, conversationType: ConversationType): void {
    if (!this.socketService.isConnected()) {
      console.warn('⚠️ Socket not connected, cannot send typing indicator');
      return;
    }

    this.socketService.emit('chat:typing:start', {
      conversationId,
      conversationType
    });
  }

  /**
   * @description Stop typing indicator
   * Follows the same pattern as event/bid socket emits
   * @param conversationId - Conversation ID
   * @param conversationType - Conversation type
   */
  stopTyping(conversationId: number, conversationType: ConversationType): void {
    if (!this.socketService.isConnected()) {
      console.warn('⚠️ Socket not connected, cannot send typing stop');
      return;
    }

    this.socketService.emit('chat:typing:stop', {
      conversationId,
      conversationType
    });
  }

  /**
   * @description Send heartbeat to keep connection alive
   * @param userId - User ID
   * @param userType - User type (admin or vendor)
   */
  sendHeartbeat(userId: number, userType: 'admin' | 'vendor'): void {
    if (!this.socketService.isConnected()) {
      return;
    }

    this.socketService.emit('chat:heartbeat', {
      userId,
      userType
    });
  }

  /**
   * @description Listen for chat message events (MESSAGE_SENT, MESSAGE_EDITED, MESSAGE_DELETED, CONVERSATION_DELETED)
   * Note: All these events come via 'chat_message' event with a 'type' field
   * @returns Observable of chat message events
   */
  onChatMessage(): Observable<any> {
    return this.socketService.socketChatData;
  }

  /**
   * @description Listen for messages read events (MESSAGES_READ - separate event from chat_message)
   * @returns Observable of messages read events
   */
  onMessagesRead(): Observable<any> {
    return this.socketService.socketChatMessagesRead;
  }

  /**
   * @description Listen for typing start events
   * @returns Observable of typing start events
   */
  onTypingStart(): Observable<any> {
    return this.socketService.socketChatTypingStart;
  }

  /**
   * @description Listen for typing stop events
   * @returns Observable of typing stop events
   */
  onTypingStop(): Observable<any> {
    return this.socketService.socketChatTypingStop;
  }

  /**
   * @description Listen for user online events
   * @returns Observable of user online events
   */
  onUserOnline(): Observable<any> {
    return this.socketService.socketChatUserOnline;
  }

  /**
   * @description Listen for user offline events
   * @returns Observable of user offline events
   */
  onUserOffline(): Observable<any> {
    return this.socketService.socketChatUserOffline;
  }

  /**
   * @description Check if socket is connected
   * @returns boolean
   */
  isConnected(): boolean {
    return this.socketService.isConnected();
  }

  /**
   * @description Get socket instance
   * Follows the same pattern as event/bid socket access
   * @returns Socket instance
   */
  getSocket(): any {
    return this.socketService.getSocket();
  }

  /**
   * @description Get socket connection status observable
   * Follows the same pattern as event/bid socket status
   * @returns Observable<boolean>
   */
  get connectionStatus(): Observable<boolean> {
    return this.socketService.connectionStatus;
  }

  /**
   * @description Check if currently in a chat room
   * @param conversationId - Conversation ID
   * @returns boolean
   */
  isInChatRoom(conversationId: number): boolean {
    return this.activeChatRooms.has(conversationId);
  }

  /**
   * @description Get all active chat rooms
   * @returns Array of conversation IDs
   */
  getActiveChatRooms(): number[] {
    return Array.from(this.activeChatRooms);
  }

  /**
   * @description Track that a chat room was joined
   * @param conversationId - Conversation ID
   */
  trackChatRoomJoined(conversationId: number): void {
    this.activeChatRooms.add(conversationId);
  }

  /**
   * @description Track that a chat room was left
   * @param conversationId - Conversation ID
   */
  trackChatRoomLeft(conversationId: number): void {
    this.activeChatRooms.delete(conversationId);
  }
}
